import { DownloadOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Grid, message } from 'antd';
import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const { useBreakpoint } = Grid;

interface ExportColumn {
  title?: string;
  dataIndex?: string;
  key?: string;
  exportRender?: (value: any, record: any) => string | number;
}

interface IProps {
  columns: ExportColumn[];
  dataSource: any[];
  fileName?: string;
  onFetchAll?: () => Promise<any[]>;
}

const getExportColumns = (columns: ExportColumn[]) =>
  columns.filter((col) => col.title && (col.dataIndex || col.exportRender));

const buildRows = (columns: ExportColumn[], dataSource: any[]) => {
  const exportCols = getExportColumns(columns);
  return dataSource.map((record) => {
    const row: Record<string, any> = {};
    exportCols.forEach((col) => {
      const header = col.title as string;
      if (col.exportRender) {
        row[header] = col.exportRender(col.dataIndex ? record[col.dataIndex] : record, record);
      } else {
        const value = record[col.dataIndex!];
        row[header] =
          value != null && typeof value === 'object'
            ? (value.fullname ?? value.name ?? value.title ?? JSON.stringify(value))
            : (value ?? '');
      }
    });
    return row;
  });
};

const downloadFile = (rows: Record<string, any>[], fileName: string, type: 'csv' | 'xlsx') => {
  if (rows.length === 0) return;
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  XLSX.writeFile(workbook, `${fileName}.${type}`, { bookType: type });
};

const exportFile = (
  columns: ExportColumn[],
  dataSource: any[],
  fileName: string,
  type: 'csv' | 'xlsx'
) => {
  const rows = buildRows(columns, dataSource);
  downloadFile(rows, fileName, type);
};

export const ExportDropdown: React.FC<IProps> = ({
  columns,
  dataSource,
  fileName = 'export',
  onFetchAll,
}) => {
  const screens = useBreakpoint();
  const [loadingAll, setLoadingAll] = useState(false);

  const handleExportAll = async (type: 'csv' | 'xlsx') => {
    if (!onFetchAll) return;
    setLoadingAll(true);
    try {
      const allData = await onFetchAll();
      const rows = buildRows(columns, allData);
      downloadFile(rows, fileName, type);
    } catch {
      message.error('Failed to fetch all data for export');
    } finally {
      setLoadingAll(false);
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'page-header',
      label: 'Current page',
      type: 'group',
      children: [
        {
          key: 'csv',
          icon: <FileTextOutlined />,
          label: 'CSV',
          onClick: () => exportFile(columns, dataSource, fileName, 'csv'),
        },
        {
          key: 'xlsx',
          icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
          label: 'Excel',
          onClick: () => exportFile(columns, dataSource, fileName, 'xlsx'),
        },
      ],
    },
    ...(onFetchAll
      ? [
          {
            key: 'all-header',
            label: 'All data',
            type: 'group' as const,
            children: [
              {
                key: 'all-csv',
                icon: <FileTextOutlined />,
                label: 'CSV',
                onClick: () => handleExportAll('csv'),
              },
              {
                key: 'all-xlsx',
                icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
                label: 'Excel',
                onClick: () => handleExportAll('xlsx'),
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <Dropdown menu={{ items, style: { minWidth: 160 } }} trigger={['click']}>
      <Button
        loading={loadingAll}
        icon={<DownloadOutlined />}
        style={{ background: '#fff7e6', borderColor: '#ffd591', color: '#d48806' }}
      >
        {screens.md ? 'Export' : null}
      </Button>
    </Dropdown>
  );
};
